import { useState, useEffect, useCallback } from 'react';

const useBrowserNotifications = () => {
  // Safe feature detection for Notification API
  const [isSupported] = useState(() => {
    return typeof window !== 'undefined' && 'Notification' in window && typeof Notification !== 'undefined';
  });
  
  const [permission, setPermission] = useState(() => {
    // Only access Notification.permission if the API is supported
    if (isSupported) {
      try {
        return Notification.permission;
      } catch (error) {
        console.warn('Error accessing Notification.permission:', error);
        return 'denied';
      }
    }
    return 'denied';
  });

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      console.warn('Browser notifications are not supported');
      return false;
    }

    if (permission === 'granted') {
      return true;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }, [isSupported, permission]);

  // Show a notification
  const showNotification = useCallback((title, options = {}) => {
    if (!isSupported || permission !== 'granted') {
      return null;
    }

    try {
      const notification = new Notification(title, {
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        ...options
      });

      // Auto-close after 5 seconds with cleanup
      const timeoutId = setTimeout(() => {
        try {
          notification.close();
        } catch (e) {
          // Notification might already be closed
        }
      }, 5000);

      // Clean up timeout if notification is manually closed
      notification.addEventListener('close', () => {
        clearTimeout(timeoutId);
      });

      // Clean up timeout if notification is clicked
      notification.addEventListener('click', () => {
        clearTimeout(timeoutId);
        notification.close();
      });

      return notification;
    } catch (error) {
      console.error('Error showing notification:', error);
      return null;
    }
  }, [isSupported, permission]);

  // Show chat message notification
  const showChatNotification = useCallback((senderName, message, tripTitle) => {
    return showNotification(`💬 ${senderName}`, {
      body: message.length > 100 ? `${message.substring(0, 100)}...` : message,
      tag: 'chat-message', // Replace previous chat notifications
      requireInteraction: false,
      actions: [
        {
          action: 'reply',
          title: 'Reply'
        },
        {
          action: 'view',
          title: 'View Trip'
        }
      ],
      data: {
        type: 'chat',
        tripTitle,
        senderName
      }
    });
  }, [showNotification]);

  // Check permission on mount
  useEffect(() => {
    if (isSupported) {
      try {
        setPermission(Notification.permission);
      } catch (error) {
        console.warn('Error checking notification permission:', error);
        setPermission('denied');
      }
    }
  }, [isSupported]);

  return {
    permission,
    isSupported,
    requestPermission,
    showNotification,
    showChatNotification
  };
};

export default useBrowserNotifications; 